/**
 * Drizzle query builders (over PowerSync/local SQLite) of the Vigía fleet
 * domain (P-6). No user_id filter: the local DB only contains what the sync
 * rules already authorize (my devices + those of my organizations, CC-40).
 */

import { and, desc, eq, isNull, sql } from "drizzle-orm";
import {
  device,
  device_alert,
  device_part,
  type PowerSyncDrizzleDB,
  remote_control_session,
} from "@vigia/powersync";

// ─── Devices ──────────────────────────────────────────────────────────────────

/** Full fleet (excludes archived unless requested). */
export function getDevicesQuery(db: PowerSyncDrizzleDB, options?: { includeArchived?: boolean }) {
  const base = db.select().from(device);
  const query = options?.includeArchived
    ? base
    : base.where(sql`COALESCE(${device.archived}, 0) = 0`);
  return query.orderBy(desc(device.last_seen_at));
}

export function getDeviceByIdQuery(db: PowerSyncDrizzleDB, deviceId: string) {
  return db.select().from(device).where(eq(device.id, deviceId));
}

// ─── Inventory parts (versioned and sealed) ──────────────────────────────────

/** The most recent part of a device (highest version). */
export function getLatestPartQuery(db: PowerSyncDrizzleDB, deviceId: string) {
  return db
    .select()
    .from(device_part)
    .where(eq(device_part.device_id, deviceId))
    .orderBy(desc(device_part.version))
    .limit(1);
}

/** All parts of a device (versioned history). */
export function getPartsQuery(db: PowerSyncDrizzleDB, deviceId: string) {
  return db
    .select()
    .from(device_part)
    .where(eq(device_part.device_id, deviceId))
    .orderBy(desc(device_part.version));
}

// ─── Alerts ───────────────────────────────────────────────────────────────────

/** Alerts of a device (by default only unresolved ones). */
export function getDeviceAlertsQuery(
  db: PowerSyncDrizzleDB,
  deviceId: string,
  options?: { includeResolved?: boolean },
) {
  const base = db.select().from(device_alert).where(eq(device_alert.device_id, deviceId));
  const query = options?.includeResolved
    ? base
    : db
        .select()
        .from(device_alert)
        .where(and(eq(device_alert.device_id, deviceId), isNull(device_alert.resolved_at)));
  return query.orderBy(desc(device_alert.raised_at));
}

/** All active alerts of the fleet (dashboard badge). */
export function getActiveAlertsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(device_alert)
    .where(isNull(device_alert.resolved_at))
    .orderBy(desc(device_alert.raised_at));
}

// ─── Remote control sessions ────────────────────────────────────────────────

/** Remote control sessions of a device (most recent first). */
export function getRemoteSessionsQuery(db: PowerSyncDrizzleDB, deviceId: string) {
  return db
    .select()
    .from(remote_control_session)
    .where(eq(remote_control_session.device_id, deviceId))
    .orderBy(desc(remote_control_session.started_at));
}
